// ===========================================
// usePermissions Hook
// ===========================================
// Provides permission-checking helpers based on the authenticated user's
// role and custom staff role permissions.

import { useAuth } from '../contexts/useAuth';

export interface UsePermissionsReturn {
  /** Check if the current user can perform a specific action on a resource */
  can: (resource: string, action?: string) => boolean;
  /** Check if the current user has any permission for a resource */
  canAny: (resource: string) => boolean;
  /** True if the user is a custom staff member (has a staffRole) */
  isStaff: boolean;
  /** True if the user has full admin access. Since roles slice 1a this is the ADMIN role only. */
  isSuperAdmin: boolean;
  /**
   * The OWNER tier (the ADMIN login role). The UI twin of the server's `ownerOnly`: use it for
   * every owner-only control ("Principal only" in MNM's specs). Never a permission.
   */
  isOwner: boolean;
  /**
   * The school OFFICE for `permission` (the UI twin of the server's `isOfficeUser`): an ADMIN
   * that is not a support account, or a staff role holding the permission. Use it where the
   * server's office rule decides (Messages), never `role === 'ADMIN'`.
   */
  isOffice: (permission: string) => boolean;
  /** Flat list of permission strings for the user's staff role, e.g. ["dashboard.read"] */
  permissions: string[];
}

export function usePermissions(): UsePermissionsReturn {
  const { user } = useAuth();

  const can = (resource: string, action: string = 'read'): boolean => {
    if (!user) return false;
    if (user.role === 'ADMIN') return true;
    // `user.isSuperAdmin` is NOT consulted: "Full access" is retired (roles plan R2) and a
    // payload cached from before slice 1a could still carry a stored flag.
    return user.staffRole?.permissions?.includes(`${resource}.${action}`) ?? false;
  };

  const canAny = (resource: string): boolean => {
    if (!user) return false;
    if (user.role === 'ADMIN') return true;
    return user.staffRole?.permissions?.some((p) => p.startsWith(`${resource}.`)) ?? false;
  };

  const isOffice = (permission: string): boolean => {
    if (!user) return false;
    if (user.role === 'ADMIN') return !user.isSupportAccount;
    return user.staffRole?.permissions?.includes(permission) ?? false;
  };

  const isStaff = user?.isStaff ?? false;
  const isOwner = user?.role === 'ADMIN';
  const isSuperAdmin = isOwner;

  return {
    can,
    canAny,
    isStaff,
    isSuperAdmin,
    isOwner,
    isOffice,
    permissions: user?.staffRole?.permissions ?? [],
  };
}
